"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import styles from "./CustomCard.module.scss";
import Text from "@components/atoms/Text";
import Button from "@components/atoms/Button";
import PriceBreakoutSection from "../PriceBreakoutSection";
import DeliveryCard from "../DeliveryCard";
import FeatureTag from "@components/atoms/FeatureTag";
import assetLoader from "@utils/internalImageLoder";
import {
  formatCurrencyINR,
  formatPrice,
  maskAmount,
} from "@utils/numberFormatter";
import { IVariantDetailsPrice } from "./CustomCard.types";
import { useRouter } from "next/navigation";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { chargesCalculationV2 } from "@helpers/customCardHelper";
import { PriceBreakup } from "@helpers/customCardHelper";
import { PageType } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { LoginSdkConfig } from "@enums/LoginSdkConfig";
import { handleLoginFlow } from "@utils/loginFlowHandler";
import CustomLink from "@components/atoms/Link";
import { getIndianCurrencyFormatForMaskedPrice } from "@helpers/getIndianCurrencyFormat";

interface CustomCardV2Props {
  data: IVariantDetailsPrice;
  isExpanded: boolean;
  onToggle: () => void;
  isLoggedIn: boolean;
  city: any;
  brandName: string;
  modelName: string;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  savingBalance?: any;
}

interface PriceBreakupData {
  charges: { label: string; value: string | null }[];
  discounts: { label: string; value: string | null }[];
  finalPrice: string;
  isExpressDelivery: boolean;
  deliveryDate: string;
  consumerSavings?: string;
}

export default function CustomCardV2({
  data,
  isExpanded,
  onToggle,
  isLoggedIn,
  city,
  brandName,
  modelName,
  isSellable,
  pageType,
  carAvailability,
  savingBalance,
}: CustomCardV2Props) {
  const { name: cityName } = getSelectedCityClient();
  const router = useRouter();
  const [priceBreakup, setPriceBreakup] = useState<PriceBreakupData>({
    charges: [],
    discounts: [],
    finalPrice: "",
    isExpressDelivery: false,
    deliveryDate: "",
  });

  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (isExpanded) {
      const fetchBreakup = async () => {
        try {
          setLoading(true);
          const queryParams = new URLSearchParams({
            slug: data.slug,
          });
          
          if (city?.name) {
            queryParams.set("city", city.name);
          }

          const response = await fetch(
            `/api/price-breakup-v2/?${queryParams.toString()}`
          );
          const result = await response.json();

          if (result.success && result.data) {
            const apiResponse = result.data;
            
            if (apiResponse?.response) {
              const defaultColor = apiResponse?.default_color || (Object.keys(apiResponse?.response) as string[])[0];
              const colorData = apiResponse?.response[defaultColor];
              
              if (!colorData) {
                console.error("No color data found in response");
                return;
              }

              let selectedPriceData;
              let isExpressDelivery = false;
              
              if (isLoggedIn && colorData.expressPrice) {
                selectedPriceData = colorData.expressPrice;
                isExpressDelivery = true;
              } else {
                selectedPriceData = colorData.adspPrice;
                isExpressDelivery = false;
              }

              if (!selectedPriceData) {
                console.error("No price data found for selected color");
                return;
              }

              if (isSellable) {
                getSellableDetailsV2(selectedPriceData, data, isLoggedIn, isExpressDelivery);
              } else {
                getNonSellableDetailsV2(selectedPriceData, isLoggedIn);
              }
            } else {
              console.error("No response data found");
            }
          } else {
            console.error("Failed to fetch price breakup:", result.error);
          }
        } catch (error) {
          console.error("Failed to fetch price breakup", error);
        } finally {
          setLoading(false);
        }
      };

      fetchBreakup();
    }
  }, [isExpanded, data.slug, city?.name, isLoggedIn]);

  return (
    <div className={styles.rootContainer}>
      {/* Section 1 - Title and Price */}
      <div className={styles.cardHeader}>
        <div className={styles.cardTopRow}>
          <CustomLink
            href={`/cars/${data.slug}`}
            className={styles.variantTitleLink}
          >
            <Text level="p" className={styles.variantTitle}>
              {data.variantName}
            </Text>
          </CustomLink>

          <div className={styles.priceGroup}>
            <Text
              level="p"
              className={
                isSellable && isLoggedIn
                  ? styles.strikePrice
                  : styles.currentPrice
              }
            >
              {`${formatPrice(data.onRoadPrice)}`}
            </Text>
            {isSellable && isLoggedIn && (
              <Text level="p" className={styles.currentPrice}>
                {isLoggedIn
                  ? `${formatPrice(data.adspPrice)}`
                  : `₹${formatPrice(data.onRoadPrice)}`}
              </Text>
            )}
          </div>
        </div>

        <div className={styles.cardSubInfoWrapper}>
          <div className={styles.cardSubInfoRow}>
            <Text level="p" className={styles.fuelText}>
              {`${data.fuelType} • ${data.transmission}`}
            </Text>
            {isSellable && (
              <Text level="p" className={styles.savingsText}>
                {`Save up to ${
                  isLoggedIn
                    ? formatCurrencyINR(data.ackoDriveSavings ?? 0)
                    : (() => {
                        const savings = data.ackoDriveSavings;
                        if (!savings) return "₹0";
                        if (typeof savings === "number") {
                          return maskAmount(savings);
                        }
                        const strValue = String(savings);
                        return strValue.startsWith("₹")
                          ? strValue
                          : `₹${strValue}`;
                      })()
                }`}
              </Text>
            )}
          </div>
        </div>

        <div className={styles.featuresWrapper}>
          <div className={styles.featuresContainer}>
            {data.featureItemDtos.map((feature) => (
              <FeatureTag
                key={feature.name}
                feature={`${feature.name} ${
                  feature.value ? ` - ${feature.value}` : ""
                }`}
              />
            ))}
          </div>
          <Button className={styles.showMoreButton} onClick={onToggle}>
            <Image
              src={`${assetLoader()}/icons/showmore.png`}
              loading="lazy"
              alt="Toggle"
              width={32}
              height={32}
              className={`${styles.icon} ${
                isExpanded ? styles.iconRotate : ""
              }`}
            />
          </Button>
        </div>
      </div>

      {/* Section 4 - Expandable Content */}
      <div
        className={`${styles.expandableWrapper} ${
          isExpanded ? styles.expanded : ""
        }`}
      >
        {loading ? (
          <div className={styles.loader}>Loading price breakup...</div>
        ) : (
          priceBreakup && (
            <div className={styles.wrapper}>
              <div className={styles.container}>
                <PriceBreakoutSection
                  charges={priceBreakup.charges}
                  discounts={priceBreakup.discounts}
                  finalPrice={priceBreakup.finalPrice}
                  consumerSavings={priceBreakup.consumerSavings}
                  isSellable={isSellable}
                  savingBalance={savingBalance}
                />

                <div className={styles.rightBox}>
                  {isSellable && (
                    <>
                      <DeliveryCard
                        expressDelivery={priceBreakup.isExpressDelivery}
                        deliveryDate={priceBreakup.deliveryDate}
                        isLoggedIn={isLoggedIn}
                        pageType={pageType}
                        brandName={brandName}
                        modelName={modelName}
                        variantName={data?.variantName}
                        carAvailability={carAvailability}
                      />
                      <div className={styles.buttonWrapper}>
                        <Button
                          className={styles.buttonOutline}
                          onClick={async () => {
                            await trackEventWithCommonProps(
                              "action",
                              EventType.TRACK_EVENT,
                              {
                                pageType: pageType || "",
                                additionalProps: {
                                  action_type: "tap_btn",
                                  action_name: "tap_btn_variants_see_details",
                                  section: "Variant",
                                  variant_name: data?.variantName || "",
                                  car_availability: carAvailability || "",
                                  vehicle_make: brandName || "",
                                  vehicle_model: modelName || "",
                                  city_name: cityName || "",
                                },
                              }
                            );
                            router.push(`/cars/${data.slug}`);
                          }}
                        >
                          {`Explore ${data.variantName}`}
                        </Button>
                        {!isLoggedIn && (
                          <Button
                            className={styles.buttonPrimary}
                            onClick={async () => {
                              await trackEventWithCommonProps(
                                "action",
                                EventType.TRACK_EVENT,
                                {
                                  pageType: pageType || "",
                                  additionalProps: {
                                    action_type: "tap_btn",
                                    action_name: "tap_btn_login_to_save",
                                    section: "Variant",
                                    variant_name: data?.variantName || "",
                                    car_availability: carAvailability || "",
                                    vehicle_make: brandName || "",
                                    vehicle_model: modelName || "",
                                    city_name: cityName || "",
                                  },
                                }
                              );

                              const mmvDetails = {
                                make: brandName,
                                model: modelName,
                                variant: data?.variantName || "",
                                color: "",
                              };
                              if (typeof window !== "undefined") {
                                sessionStorage.setItem(
                                  "loginType",
                                  "Reveal price"
                                );
                                sessionStorage.setItem("flowType", "New Car");
                                localStorage.setItem(
                                  "MMV",
                                  JSON.stringify({
                                    make: brandName,
                                    model: modelName,
                                    variant: data?.variantName || "",
                                    color: "",
                                  })
                                );
                              }
                              const loginUrl = getLoginUrl(
                                addCaptureIntentIfNeeded(
                                  window.location.href,
                                  mmvDetails,
                                  "Reveal price",
                                  pageType,
                                  "New Car"
                                )
                              );
                              await handleLoginFlow(
                                loginUrl,
                                LoginSdkConfig.AD_PRICE
                              );
                            }}
                          >
                            Check ACKO Drive price
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                  {!isSellable && (
                    <div className={styles.nonSellableButtonWrapper}>
                      <Button
                        className={styles.buttonOutline}
                        onClick={async () => {
                          await trackEventWithCommonProps(
                            "action",
                            EventType.TRACK_EVENT,
                            {
                              pageType: pageType || "",
                              additionalProps: {
                                action_type: "tap_btn",
                                action_name: "tap_btn_variants_see_details",
                                section: "Variant",
                                variant_name: data?.variantName || "",
                                car_availability: carAvailability || "",
                                vehicle_make: brandName || "",
                                vehicle_model: modelName || "",
                                city_name: cityName || "",
                              },
                            }
                          );
                          router.push(`/cars/${data.slug}`);
                        }}
                      >
                        {`Explore ${data.variantName}`}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );

  function getSellableDetailsV2(priceData: PriceBreakup, data: any, isLoggedIn: boolean, isExpressDelivery: boolean) {
    const ackoDriveSavings = priceData.acko_drive_savings;

    const discounts = [
      {
        label: `On-road price in ${city.name}`,
        value: priceData.market_price
          ? formatCurrencyINR(Number(priceData.market_price))
          : "",
      },
      ...(ackoDriveSavings &&
      (isLoggedIn ? Number(ackoDriveSavings) > 0 : true)
        ? [
            {
              label: "ACKO Drive discount",
              value: isLoggedIn
                ? `-${formatCurrencyINR(Number(ackoDriveSavings))}`
                : `-₹${getIndianCurrencyFormatForMaskedPrice(
                    ackoDriveSavings
                  )}`,
            },
          ]
        : []),
    ];

    const finalPriceValue =
      priceData.acko_drive_price ||
      priceData.onroad_price ||
      priceData.market_price;
    const finalPrice = isLoggedIn
      ? formatCurrencyINR(Number(finalPriceValue) || 0)
      : `₹${getIndianCurrencyFormatForMaskedPrice(finalPriceValue)}`;
    const deliveryDate =
      priceData.additional_attributes?.delivery_days || priceData.additional_attributes?.waiting_period || "";

    setPriceBreakup({
      charges: chargesCalculationV2(priceData),
      discounts: discounts,
      consumerSavings: Number(priceData.discount_components?.consumerdiscount)
        ? formatCurrencyINR(Number(priceData.discount_components?.consumerdiscount))
        : undefined,
      finalPrice: finalPrice,
      isExpressDelivery: isExpressDelivery,
      deliveryDate: deliveryDate,
    });
  }

  function getNonSellableDetailsV2(priceData: PriceBreakup, isLoggedIn: boolean) {
    const ackoDriveSavings = priceData.acko_drive_savings;

    const discounts = [
      {
        label: `On-road price in ${city.name}`,
        value: priceData.market_price
          ? formatCurrencyINR(Number(priceData.market_price))
          : "",
      },
      ...(ackoDriveSavings &&
      (isLoggedIn ? Number(ackoDriveSavings) > 0 : true)
        ? [
            {
              label: "ACKO Drive discount",
              value: isLoggedIn
                ? `-${formatCurrencyINR(Number(ackoDriveSavings))}`
                : `-₹${getIndianCurrencyFormatForMaskedPrice(
                    ackoDriveSavings
                  )}`,
            },
          ]
        : []),
    ];

    const deliveryDate =
     priceData.additional_attributes?.delivery_days || priceData.additional_attributes?.waiting_period || "";

    const finalPriceValue =
      priceData.acko_drive_price ||
      priceData.onroad_price ||
      priceData.market_price;
    const finalPrice = isLoggedIn
      ? formatCurrencyINR(Number(finalPriceValue) || 0)
      : `₹${getIndianCurrencyFormatForMaskedPrice(finalPriceValue)}`;

    setPriceBreakup({
      charges: chargesCalculationV2(priceData),
      discounts: discounts,
      consumerSavings: Number(priceData.discount_components?.consumerdiscount)
        ? formatCurrencyINR(Number(priceData.discount_components?.consumerdiscount))
        : undefined,
      finalPrice: finalPrice,
      isExpressDelivery: false,
      deliveryDate: deliveryDate,
    });
  }
}

